import {
  Counter,
  Histogram,
  Meter,
  UpDownCounter,
  metrics,
  Attributes,
} from "@opentelemetry/api";
import { TelemetryApi, TelemetryRow } from "./types";

type TelemetrySubscriber = Pick<TelemetryApi, "subscribe" | "getTelemetrySnapshot">;
type TelemetryEmitter = {
  on: (event: "telemetry_row", listener: (row: TelemetryRow) => void) => unknown;
  off?: (event: "telemetry_row", listener: (row: TelemetryRow) => void) => unknown;
  removeListener?: (
    event: "telemetry_row",
    listener: (row: TelemetryRow) => void
  ) => unknown;
  getTelemetrySnapshot: () => ReturnType<TelemetryApi["getTelemetrySnapshot"]>;
};
type TelemetryLike = TelemetrySubscriber | TelemetryEmitter;

export interface OpenTelemetryBridgeOptions {
  meter?: Meter;
  meterName?: string;
}

const toAttributes = (tags?: TelemetryRow["tags"]): Attributes | undefined => {
  if (!tags) {
    return undefined;
  }

  const attributes: Attributes = {};
  Object.keys(tags).forEach((key) => {
    attributes[key] = tags[key];
  });

  return attributes;
};

const keyForGauge = (metric: string, tags?: TelemetryRow["tags"]) => {
  if (!tags || Object.keys(tags).length === 0) {
    return metric;
  }

  return `${metric}:${JSON.stringify(tags)}`;
};

export const attachOpenTelemetryBridge = (
  telemetry: TelemetryLike,
  options: OpenTelemetryBridgeOptions = {}
): (() => void) => {
  const meter = options.meter ?? metrics.getMeter(options.meterName ?? "sofya.transcription.telemetry");

  const counterInstruments = new Map<string, Counter>();
  const histogramInstruments = new Map<string, Histogram>();
  const gaugeInstruments = new Map<string, UpDownCounter>();
  const gaugeValues = new Map<string, number>();

  const getCounter = (metric: string): Counter => {
    let instrument = counterInstruments.get(metric);
    if (!instrument) {
      instrument = meter.createCounter(metric);
      counterInstruments.set(metric, instrument);
    }
    return instrument;
  };

  const getHistogram = (metric: string): Histogram => {
    let instrument = histogramInstruments.get(metric);
    if (!instrument) {
      instrument = meter.createHistogram(metric);
      histogramInstruments.set(metric, instrument);
    }
    return instrument;
  };

  const getGauge = (metric: string): UpDownCounter => {
    let instrument = gaugeInstruments.get(metric);
    if (!instrument) {
      instrument = meter.createUpDownCounter(metric);
      gaugeInstruments.set(metric, instrument);
    }
    return instrument;
  };

  const handleRow = (row: TelemetryRow) => {
    const attributes = toAttributes(row.tags);

    if (row.type === "counter") {
      getCounter(row.metric).add(row.value, attributes);
      return;
    }

    if (row.type === "histogram") {
      getHistogram(row.metric).record(row.value, attributes);
      return;
    }

    if (row.type === "event") {
      getCounter(row.metric).add(1, attributes);
      return;
    }

    const gauge = getGauge(row.metric);
    const gaugeKey = keyForGauge(row.metric, row.tags);
    const previousValue = gaugeValues.get(gaugeKey) ?? 0;
    const delta = row.value - previousValue;
    if (delta !== 0) {
      gauge.add(delta, attributes);
      gaugeValues.set(gaugeKey, row.value);
    }
  };

  const unsubscribe =
    "subscribe" in telemetry
      ? telemetry.subscribe(handleRow)
      : (() => {
          telemetry.on("telemetry_row", handleRow);
          return () => {
            if (typeof telemetry.off === "function") {
              telemetry.off("telemetry_row", handleRow);
              return;
            }

            if (typeof telemetry.removeListener === "function") {
              telemetry.removeListener("telemetry_row", handleRow);
            }
          };
        })();

  // Seed current gauge state at bridge creation.
  const snapshot = telemetry.getTelemetrySnapshot();
  Object.keys(snapshot.gauges).forEach((metric) => {
    const value = snapshot.gauges[metric];
    const gauge = getGauge(metric);
    gauge.add(value);
    gaugeValues.set(metric, value);
  });

  return () => {
    unsubscribe();
    counterInstruments.clear();
    histogramInstruments.clear();
    gaugeInstruments.clear();
    gaugeValues.clear();
  };
};
